'use client';

import {
	defaultState,
	type ReplaceVirtuallyFormState,
	StepMap,
	steps,
} from './constants';

import React, { Dispatch, ReactNode, useMemo, useState } from 'react';
import { addressGraph } from './steps/AddressStep/routing';
import { contactInfoStepGraph } from './steps/ContactInfoStep/routing';
import { finalizeHubspotDeal } from './helpers/finalizeHubspotDeal';
import { Form } from '@/components/forms/Form';
import { FormContext_Actions } from '@/components/forms/FormContext';
import { FormSubmissionAnimation } from '@/components/FormSubmissionAnimation';
import { getContact } from './helpers/getContact';
import { messageStepGraph } from './steps/MessageStep/routing';
import { pictureStepGraph } from './steps/PictureStep/routing';
import { RemoveScroll } from 'react-remove-scroll';
import { reventEvent } from './helpers/reventEvent';
import { rootReducer, type ReplaceVirtuallyFormActionMap } from './reducer';
import { systemAgeStepGraph } from './steps/SystemAgeStep/routing';
import { systemIssuesStepGraph } from './steps/SystemIssuesStep/routing';
import { urgencyStepGraph } from './steps/UrgencyStep/routing';
import { useSplitFormFlow } from '@/hooks/useSplitFormFlow';
import sendEmail, { UserData } from '@/helpers/sendEmail';
import { buyingDecisionStepGraph } from '@/app/repair/flow/components/RepairForm/steps/BuyingDecisionStep';

export interface ReplaceVirtuallyFormControllerProps {
	children: ReactNode;
}

const ReplaceVirtuallyFormController = (
	props: ReplaceVirtuallyFormControllerProps,
) => {
	const { children } = props;
	const [isSubmitting, setIsSubmitting] = useState(false);

	const onSuccess = () => {
		window.location.href = '/replace/virtual/confirmation/';
	};

	const handleSubmit = async (
		state: ReplaceVirtuallyFormState,
		dispatch: Dispatch<ReplaceVirtuallyFormActionMap>,
	) => {
		dispatch({ type: FormContext_Actions.SET_IN_PROGRESS, payload: null });

		setIsSubmitting(true);

		try {
			await getContact(state, dispatch);
		} catch (error) {
			dispatch({
				type: FormContext_Actions.SET_FIELD_ERRORS,
				payload: {
					...state.fieldErrors,
					email:
						(error as Error).message ||
						'An error occurred while creating the contact',
				},
			});
			dispatch({ type: FormContext_Actions.SET_IDLE, payload: null });
			dispatch({
				type: FormContext_Actions.SHOW_VALIDATION_ERRORS,
				payload: true,
			});
			setIsSubmitting(false);
			return;
		}

		finalizeHubspotDeal(state, dispatch).then(async (hubspotResult) => {
			await reventEvent(state, hubspotResult.dealInfo);

			const userData: UserData = {
				referenceID: state.referenceId,
				mtaAnonymousID: state.sessionId,
				firstName: state.firstName,
				lastName: state.lastName,
				email: state.email,
			};

			await sendEmail('virtual-replace', userData);

			const { uniqueUrl, dealId, leadPaymentUrl } =
				hubspotResult.dealInfo ?? {};

			// update deal with unique_url
			await fetch(`/api/hubspot/deal?id=${dealId}`, {
				method: 'PATCH',
				body: JSON.stringify({
					unique_url: uniqueUrl,
					lead_payment_url: leadPaymentUrl,
				}),
			});

			if (hubspotResult.success) {
				onSuccess();
			}
		});
	};

	const defaultFormGraph = useMemo(
		() => ({
			...addressGraph,
			...contactInfoStepGraph,
			...systemIssuesStepGraph,
			...pictureStepGraph,
			...buyingDecisionStepGraph,
			...messageStepGraph,
			...systemAgeStepGraph,
			...urgencyStepGraph,
		}),
		[],
	);

	const { formGraph, isLoading, updatedState } = useSplitFormFlow(
		'VirtualReplaceFlow',
		defaultFormGraph,
		defaultState,
		steps,
	);

	if (!isLoading) {
		return (
			<Form<StepMap, ReplaceVirtuallyFormState, ReplaceVirtuallyFormActionMap>
				name="ReplaceVirtuallyForm"
				onSubmit={handleSubmit}
				defaultState={updatedState}
				reducer={rootReducer}
				graph={formGraph}
				className="flex grow flex-col"
			>
				{isSubmitting && (
					// Important! This conditional should be placed over the children for better z-index management
					<RemoveScroll>
						<FormSubmissionAnimation />
					</RemoveScroll>
				)}
				{children}
			</Form>
		);
	}
};

export default ReplaceVirtuallyFormController;
